import { useState } from 'react';
import { CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import axios, { endpoints } from 'src/utils/axios';
import Iconify from 'src/components/iconify';
import { StopLocation } from './types';

type LocationFieldProps = {
  value: StopLocation;
  onChange: (value: StopLocation) => void;
  picking: boolean;
  onTogglePick: () => void;
  placeholder?: string;
};

export default function LocationField({
  value,
  onChange,
  picking,
  onTogglePick,
  placeholder = 'Address, city or zip',
}: LocationFieldProps) {
  const [resolving, setResolving] = useState(false);

  const resolved = value.lat !== null && value.lng !== null;

  const handleLocate = () => {
    if (!value.address.trim() || resolving) {
      return;
    }
    setResolving(true);
    axios
      .get(`${endpoints.routeBuddy.geocode}?address=${encodeURIComponent(value.address.trim())}`)
      .then((response) => {
        onChange({ ...value, lat: response.data.lat, lng: response.data.lng });
      })
      .catch(() => {
        enqueueSnackbar(`Could not find "${value.address}", try a fuller address or pick it on the map`, {
          variant: 'error',
        });
      })
      .finally(() => setResolving(false));
  };

  return (
    <TextField
      size="small"
      autoComplete="off"
      sx={{ display: 'flex' }}
      value={value.address}
      onChange={(e) => onChange({ address: e.target.value, lat: null, lng: null })}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleLocate();
        }
      }}
      onBlur={() => {
        if (!resolved && value.address.trim()) {
          handleLocate();
        }
      }}
      placeholder={placeholder}
      helperText={resolved ? `${value.lat?.toFixed(4)}, ${value.lng?.toFixed(4)}` : undefined}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {resolving && <CircularProgress size={18} sx={{ mr: 1 }} />}
            {!resolving && resolved && (
              <Iconify icon="mdi:check-circle" sx={{ color: 'success.main', mr: 0.5 }} />
            )}
            {!resolving && !resolved && (
              <Tooltip title="Find this address">
                <IconButton size="small" onClick={handleLocate}>
                  <Iconify icon="mdi:magnify" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={picking ? 'Click the map to set this location' : 'Pick on the map'}>
              <IconButton size="small" color={picking ? 'primary' : 'default'} onClick={onTogglePick}>
                <Iconify icon="mdi:map-marker-outline" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}
